import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { expendituresService } from "../services/expendituresService";
// P2P v1 — read-only: what Xero already recorded about this payment
import { getPaymentStatus } from "../services/xeroBillsService";
import s3Service from "../services/s3Service";
import styles from "../assets/expenseAccountPay.module.css";

const ExpenseAccountPay = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [propertySearchResults, setPropertySearchResults] = useState([]);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  
  // Date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const dueDatePickerRef = useRef(null);
  
  // Track current month/year for each date picker
  const [datePickerMonth, setDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [datePickerYear, setDatePickerYear] = useState(new Date().getFullYear());
  const [dueDatePickerMonth, setDueDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [dueDatePickerYear, setDueDatePickerYear] = useState(new Date().getFullYear());
  const [expenditure, setExpenditure] = useState({
    id: "",
    property_id: "",
    property_name: "",
    type_id: "",
    bank_account_id: "",
    creditor: "",
    ticket_id: "",
    date: "",
    due_date: "",
    amount: "",
    amount_paid: "",
    invoice_number: "",
    purchase_order_number: "",
    description: "",
    file: null,
    confirmation_file: null,
    status: 0,
    isPay: true
  });

  // Dropdown options
  const [types, setTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [properties, setProperties] = useState([]);

  // File states
  const [existingFile, setExistingFile] = useState(null);
  const [existingConfirmationFile, setExistingConfirmationFile] = useState(null);

  // What Xero says about this bill's payment. Read-only reference, never applied to the form.
  const [xeroPayment, setXeroPayment] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [newConfirmationFile, setNewConfirmationFile] = useState(null);

  // Memoized loadData function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Real API call
      console.log("📡 [FRONTEND] Loading pay form data for expense ID:", id);
      const response = await expendituresService.getPayFormData(id);
      console.log("✅ [FRONTEND] Pay form data response:", response);
      
      if (response.success) {
        const { expense, formData } = response.data;
        
        // Laravel behavior: Pre-fill amount_paid with amount when paying
        // Laravel: Form::text('amount_paid', isset($expenditure) ? $expenditure->amount : null)
        // This ensures when user saves, amount_paid >= amount, so expense moves to Expenditures
        setExpenditure({
          ...expense,
          amount_paid: expense.amount || "", // Always pre-fill with full amount when paying (matching Laravel)
          isPay: true
        });
        
        if (expense.file) {
          setExistingFile(expense.file);
        }
        
        if (expense.confirmation_file) {
          setExistingConfirmationFile(expense.confirmation_file);
        }
        
        // Set dropdown data
        setTypes(formData.types || []);
        setBankAccounts(formData.bankAccounts || []);
        setProperties(formData.properties || []);

        // What Xero already recorded for this bill. Read-only and shown alongside — nothing
        // is filled in from it, because which MyGlobe bank account corresponds to a Xero one
        // is a decision nobody has made yet. Showing it means the person choosing does not
        // have to go and look it up.
        getPaymentStatus([id])
          .then((res) => {
            if (res?.success) setXeroPayment(res.data?.[id] || null);
          })
          .catch(() => {
            /* supplementary only — the Pay screen works without it */
          });
      } else {
        toast.error("Failed to load expense data");
        setExpenditure({});
        setTypes([]);
        setBankAccounts([]);
        setProperties([]);
      }
    } catch (error) {
      console.error("Error loading pay form data:", error);
      toast.error("Failed to load expense data. Please try again.");
      setExpenditure({});
      setTypes([]);
      setBankAccounts([]);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load data on component mount
  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [loadData]);

  // Click outside handler for date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
      if (showDueDatePicker && dueDatePickerRef.current && !dueDatePickerRef.current.contains(event.target)) {
        setShowDueDatePicker(false);
      }
      if (showPropertyDropdown && !event.target.closest('.property-typeahead')) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, showDueDatePicker, showPropertyDropdown]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setExpenditure(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Handle property search
    if (field === 'property_name') {
      handlePropertySearch(value);
    }
  };

  // Property search function
  const handlePropertySearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setPropertySearchResults([]);
      setShowPropertyDropdown(false);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/properties/search?q=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      
      if (data.success) {
        setPropertySearchResults(data.properties || []);
        setShowPropertyDropdown(true);
      }
    } catch (error) {
      console.error('Error searching properties:', error);
      setPropertySearchResults([]);
      setShowPropertyDropdown(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (property) => {
    setExpenditure(prev => ({
      ...prev,
      property_name: property.name,
      property_id: property.id
    }));
    setPropertySearchResults([]);
    setShowPropertyDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyDropdown && !event.target.closest(`.${styles.propertySearchContainer}`)) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyDropdown]);

  // Handle file changes
  const handleFileChange = async (field, file) => {
    if (!file) return;

    try {
      // Upload file to S3
      // Laravel path structure: public/expenditures/files/{filename}
      // Actual S3 bucket structure: public/expenditures/files/{filename}
      const fileKey = await s3Service.uploadDocument(file, {
        folder: "public/expenditures/files", // Match actual S3 bucket structure
        useLaravelStructure: true, // No timestamp prefix
        onProgress: (progress) => {
          console.log(`${field} upload progress:`, progress);
        }
      });

      if (field === 'file') {
        setNewFile(file);
        setExpenditure(prev => ({ ...prev, fileKey }));
      } else if (field === 'confirmation_file') {
        setNewConfirmationFile(file);
        setExpenditure(prev => ({ ...prev, confirmationFileKey: fileKey }));
      }

      toast.success(`${field === 'confirmation_file' ? 'Payment confirmation' : 'Invoice'} uploaded successfully!`);
    } catch (error) {
      console.error(`Error uploading ${field}:`, error);
      toast.error(`Failed to upload ${field === 'confirmation_file' ? 'payment confirmation' : 'invoice'}`);
    }
  };

  // Handle file removal
  const handleFileRemove = (field) => {
    if (field === 'file') {
      setExistingFile(null);
      setNewFile(null);
      // Also clear the file field in the expenditure state
      setExpenditure(prev => ({ ...prev, file: null }));
      // Reset the file input element
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
    } else if (field === 'confirmation_file') {
      setExistingConfirmationFile(null);
      setNewConfirmationFile(null);
      // Also clear the confirmation_file field in the expenditure state
      setExpenditure(prev => ({ ...prev, confirmation_file: null }));
      // Reset the confirmation file input element
      const fileInputs = document.querySelectorAll('input[type="file"]');
      if (fileInputs.length > 1) {
        fileInputs[1].value = '';
      }
    }
  };

  // Download file
  const handleFileDownload = async (field) => {
    try {
      const fileName = field === 'file' ? expenditure.file : expenditure.confirmation_file;
      if (!fileName) {
        toast.error("No file available for download");
        return;
      }
      
      // Use expendituresService to get presigned URL and trigger direct download
      await expendituresService.downloadFile(id, field);
      toast.success("File download started!");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // Date picker handlers
  const handleDateClick = (field) => {
    if (field === 'date') {
      setShowDatePicker(!showDatePicker);
      setShowDueDatePicker(false);
    } else if (field === 'due_date') {
      setShowDueDatePicker(!showDueDatePicker);
      setShowDatePicker(false);
    }
  };

  const selectDate = (field, day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    setExpenditure(prev => ({ ...prev, [field]: formattedDate }));
    
    if (field === 'date') {
      setShowDatePicker(false);
    } else if (field === 'due_date') {
      setShowDueDatePicker(false);
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Month names array (shared)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

  const generateCalendarDays = (month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    
    const days = [];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return {
      days,
      monthName: monthNames[month - 1], 
      year: year,
      month: month
    };
  };

  // Navigation handlers for date picker
  const handlePrevMonth = (field) => {
    if (field === 'date') {
      if (datePickerMonth === 1) {
        setDatePickerMonth(12);
        setDatePickerYear(datePickerYear - 1);
      } else {
        setDatePickerMonth(datePickerMonth - 1);
      }
    } else if (field === 'due_date') {
      if (dueDatePickerMonth === 1) {
        setDueDatePickerMonth(12);
        setDueDatePickerYear(dueDatePickerYear - 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth - 1);
      }
    }
  };

  const handleNextMonth = (field) => {
    if (field === 'date') {
      if (datePickerMonth === 12) {
        setDatePickerMonth(1);
        setDatePickerYear(datePickerYear + 1);
      } else {
        setDatePickerMonth(datePickerMonth + 1);
      }
    } else if (field === 'due_date') {
      if (dueDatePickerMonth === 12) {
        setDueDatePickerMonth(1);
        setDueDatePickerYear(dueDatePickerYear + 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth + 1);
      }
    }
  };

  const handleMonthChange = (field, month) => {
    if (field === 'date') {
      setDatePickerMonth(parseInt(month));
    } else if (field === 'due_date') {
      setDueDatePickerMonth(parseInt(month));
    }
  };

  const handleYearChange = (field, year) => {
    if (field === 'date') {
      setDatePickerYear(parseInt(year));
    } else if (field === 'due_date') {
      setDueDatePickerYear(parseInt(year));
    }
  };

  // Generate year options (current year ± 10 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }

  const datePickerCalendar = generateCalendarDays(datePickerMonth, datePickerYear);
  const dueDatePickerCalendar = generateCalendarDays(dueDatePickerMonth, dueDatePickerYear);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!expenditure.property_name?.trim()) {
      newErrors.property_name = "Property is required";
    }
    if (!expenditure.type_id) {
      newErrors.type_id = "Type is required";
    }
    if (!expenditure.bank_account_id) {
      newErrors.bank_account_id = "Bank account is required";
    }
    if (!expenditure.creditor?.trim()) {
      newErrors.creditor = "Company is required";
    }
    if (!expenditure.date) {
      newErrors.date = "Date paid is required";
    }
    if (!expenditure.due_date) {
      newErrors.due_date = "Due date is required";
    }
    if (!expenditure.amount || parseFloat(expenditure.amount) <= 0) {
      newErrors.amount = "Amount is required and must be greater than 0";
    }
    if (!expenditure.amount_paid || parseFloat(expenditure.amount_paid) <= 0) {
      newErrors.amount_paid = "Total paid is required and must be greater than 0";
    }
    if (!expenditure.invoice_number?.trim()) {
      newErrors.invoice_number = "Invoice number is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Add all form fields
      Object.keys(expenditure).forEach(key => {
        if (expenditure[key] !== null && expenditure[key] !== undefined) {
          formData.append(key, expenditure[key]);
        }
      });
      
      // Add S3 file keys if available (files already uploaded to S3)
      // Backend expects fileKey and confirmationFileKey (strings), not File objects
      if (expenditure.fileKey) {
        formData.append('fileKey', expenditure.fileKey);
        console.log("[EXPENSE_ACCOUNT_PAY] Sending fileKey:", expenditure.fileKey);
      }
      if (expenditure.confirmationFileKey) {
        formData.append('confirmationFileKey', expenditure.confirmationFileKey);
        console.log("[EXPENSE_ACCOUNT_PAY] Sending confirmationFileKey:", expenditure.confirmationFileKey);
      }
      
      // Add flags for file removal
      if (!existingFile && !expenditure.fileKey) {
        formData.append('remove_file', '1');
      }
      if (!existingConfirmationFile && !expenditure.confirmationFileKey) {
        formData.append('remove_confirmation_file', '1');
      }
      
      // Debug: Log what we're sending
      console.log("📡 [FRONTEND] Submitting payment data for expense ID:", id);
      console.log("📡 [FRONTEND] Expenditure state:", expenditure);
      console.log("📡 [FRONTEND] amount_paid value:", expenditure.amount_paid);
      console.log("📡 [FRONTEND] amount value:", expenditure.amount);
      console.log("📡 [FRONTEND] bank_account_id value:", expenditure.bank_account_id);
      
      // Verify key fields are in FormData
      const amountPaidValue = formData.get('amount_paid');
      const bankAccountIdValue = formData.get('bank_account_id');
      console.log("📡 [FRONTEND] FormData amount_paid:", amountPaidValue);
      console.log("📡 [FRONTEND] FormData bank_account_id:", bankAccountIdValue);
      
      const response = await expendituresService.updatePayment(id, formData);
      console.log("✅ [FRONTEND] Payment update response:", response);
      
      if (response.success) {
        toast.success("Payment saved successfully!");
        navigate("/finance/expense-account");
      } else {
        toast.error(response.message || "Failed to save payment");
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/finance/expense-account");
  };

  if (loading && !expenditure.id) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading expense data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/finance/expense-account">Finance</Link>
        <span className={styles.separator}>/</span>
        <Link to="/finance/expense-account">Expense Account</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Pay</span>
      </div>

      <div className={styles.mainSection}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panel}>
              <div className={styles.panelHeading}>
                <h3>Pay</h3>
              </div>
              <div className={styles.panelBody}>
                <form onSubmit={handleSubmit}>
                  {/* Hidden fields */}
                  <input
                    type="hidden"
                    value={expenditure.property_id || ""}
                    onChange={(e) => handleInputChange('property_id', e.target.value)}
                  />
                  <input
                    type="hidden"
                    value={expenditure.isPay || true}
                    onChange={(e) => handleInputChange('isPay', e.target.value)}
                  />

                  {/* Property Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Property</label>
                    <div className={styles.colSm5}>
                      <div className={styles.propertySearchContainer}>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={expenditure.property_name || ""}
                          onChange={(e) => handleInputChange('property_name', e.target.value)}
                          placeholder="Search for property..."
                          onFocus={() => {
                            if (propertySearchResults.length > 0) {
                              setShowPropertyDropdown(true);
                            }
                          }}
                        />
                        {showPropertyDropdown && propertySearchResults.length > 0 && (
                          <div className={styles.propertyDropdown}>
                            {propertySearchResults.map((property) => (
                              <div
                                key={property.id}
                                className={styles.propertyOption}
                                onClick={() => handlePropertySelect(property)}
                              >
                                <div className={styles.propertyName}>{property.name}</div>
                                <div className={styles.propertyDetails}>
                                  {property.property_id} • {property.address_1} • {property.postcode}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.property_name && (
                        <div className={styles.errorMessage}>{errors.property_name}</div>
                      )}
                    </div>
                  </div>

                  {/* Type Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Type</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        required
                        value={expenditure.type_id || ""}
                        onChange={(e) => handleInputChange('type_id', e.target.value)}
                      >
                        <option value="">Please Select</option>
                        {types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.type_id && (
                        <div className={styles.errorMessage}>{errors.type_id}</div>
                      )}
                    </div>
                  </div>

                  {/* What Xero already recorded for this bill.
                      Read-only and deliberately not applied to the form: which MyGlobe bank
                      account corresponds to a Xero one is a decision nobody has made, and a
                      pre-filled guess would look like somebody's choice. Shown here so the
                      person selecting below does not have to go and look it up in Xero. */}
                  {xeroPayment && Number(xeroPayment.amount_paid) > 0 && (
                    <div
                      className={styles.formGroup}
                      style={{
                        background: '#e7f5ec',
                        border: '1px solid #b7e0c6',
                        borderRadius: '4px',
                        padding: '10px 14px',
                        marginBottom: '14px',
                      }}
                    >
                      <strong style={{ color: '#146c43' }}>Already paid in Xero</strong>
                      <div style={{ marginTop: '4px' }}>
                        <strong>£{Number(xeroPayment.amount_paid).toFixed(2)}</strong>
                        {xeroPayment.payment_date && (
                          <> on {new Date(xeroPayment.payment_date).toLocaleDateString('en-GB')}</>
                        )}
                        {xeroPayment.payment_account_name && (
                          <> from <strong>{xeroPayment.payment_account_name}</strong></>
                        )}
                        {xeroPayment.xero_url && (
                          <>
                            {' '}
                            <a href={xeroPayment.xero_url} target="_blank" rel="noopener noreferrer">
                              View in Xero
                            </a>
                          </>
                        )}
                      </div>
                      <small style={{ color: '#5a6d61' }}>
                        For reference — nothing below has been filled in from this.
                      </small>
                    </div>
                  )}

                  {/* Bank Account Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>GG Bank Account</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        required
                        value={expenditure.bank_account_id || ""}
                        onChange={(e) => handleInputChange('bank_account_id', e.target.value)}
                      >
                        <option value="">Please Select</option>
                        {bankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                      </select>
                      {errors.bank_account_id && (
                        <div className={styles.errorMessage}>{errors.bank_account_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Company Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Company</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.creditor || ""}
                        onChange={(e) => handleInputChange('creditor', e.target.value)}
                      />
                      {errors.creditor && (
                        <div className={styles.errorMessage}>{errors.creditor}</div>
                      )}
                    </div>
                  </div>

                  {/* Ticket ID Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Ticket Id</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        maxLength={9}
                        value={expenditure.ticket_id || ""}
                        onChange={(e) => handleInputChange('ticket_id', e.target.value)}
                      />
                      {errors.ticket_id && (
                        <div className={styles.errorMessage}>{errors.ticket_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Date Paid Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Date Paid</label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={datePickerRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          required
                          placeholder="DD/MM/YYYY"
                          value={expenditure.date || ""}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          onClick={() => handleDateClick('date')}
                          readOnly
                        />
                        {showDatePicker && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handlePrevMonth('date')}
                                aria-label="Previous month"
                              >
                                ‹
                              </button>
                              <div className={styles.datePickerMonthYear}>
                                <select
                                  className={styles.datePickerSelect}
                                  value={datePickerMonth}
                                  onChange={(e) => handleMonthChange('date', e.target.value)}
                                >
                                  {monthNames.map((month, index) => (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={styles.datePickerSelect}
                                  value={datePickerYear}
                                  onChange={(e) => handleYearChange('date', e.target.value)}
                                >
                                  {years.map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handleNextMonth('date')}
                                aria-label="Next month"
                              >
                                ›
                              </button>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {datePickerCalendar.days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate('date', day, datePickerCalendar.month, datePickerCalendar.year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.date && (
                        <div className={styles.errorMessage}>{errors.date}</div>
                      )}
                    </div>
                  </div>

                  {/* Due Date Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Due Date</label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={dueDatePickerRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          required
                          placeholder="DD/MM/YYYY"
                          value={expenditure.due_date || ""}
                          onChange={(e) => handleInputChange('due_date', e.target.value)}
                          onClick={() => handleDateClick('due_date')}
                          readOnly
                        />
                        {showDueDatePicker && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handlePrevMonth('due_date')}
                                aria-label="Previous month"
                              >
                                ‹
                              </button>
                              <div className={styles.datePickerMonthYear}>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerMonth}
                                  onChange={(e) => handleMonthChange('due_date', e.target.value)}
                                >
                                  {monthNames.map((month, index) => (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerYear}
                                  onChange={(e) => handleYearChange('due_date', e.target.value)}
                                >
                                  {years.map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handleNextMonth('due_date')}
                                aria-label="Next month"
                              >
                                ›
                              </button>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {dueDatePickerCalendar.days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate('due_date', day, dueDatePickerCalendar.month, dueDatePickerCalendar.year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.due_date && (
                        <div className={styles.errorMessage}>{errors.due_date}</div>
                      )}
                    </div>
                  </div>

                  {/* Amount Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Amount</label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.formControl}
                        required
                        value={expenditure.amount || ""}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                      />
                      {errors.amount && (
                        <div className={styles.errorMessage}>{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Total Paid Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Total Paid</label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.formControl}
                        required
                        value={expenditure.amount_paid || ""}
                        onChange={(e) => handleInputChange('amount_paid', e.target.value)}
                      />
                      {errors.amount_paid && (
                        <div className={styles.errorMessage}>{errors.amount_paid}</div>
                      )}
                    </div>
                  </div>

                  {/* Invoice Number Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Invoice number</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.invoice_number || ""}
                        onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                      />
                      {errors.invoice_number && (
                        <div className={styles.errorMessage}>{errors.invoice_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Purchase Order Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Purchase Order</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={expenditure.purchase_order_number || ""}
                        onChange={(e) => handleInputChange('purchase_order_number', e.target.value)}
                      />
                      {errors.purchase_order_number && (
                        <div className={styles.errorMessage}>{errors.purchase_order_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Description</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows={3}
                        value={expenditure.description || ""}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                      />
                      {errors.description && (
                        <div className={styles.errorMessage}>{errors.description}</div>
                      )}
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Invoice</label>
                    <div className={styles.colSm5}>
                      {existingFile && !newFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleFileDownload('file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              className={styles.formControl}
                              onChange={(e) => handleFileChange('file', e.target.files[0])}
                            />
                            {newFile && (
                              <button
                                type="button"
                                className={styles.btnDanger}
                                onClick={() => handleFileRemove('file')}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.file && (
                        <div className={styles.errorMessage}>{errors.file}</div>
                      )}
                    </div>
                  </div>

                  {/* Confirmation File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Payment Confirmation</label>
                    <div className={styles.colSm5}>
                      {existingConfirmationFile && !newConfirmationFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingConfirmationFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleFileDownload('confirmation_file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('confirmation_file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              className={styles.formControl}
                              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange('confirmation_file', e.target.files[0])}
                            />
                            {newConfirmationFile && (
                              <button
                                type="button"
                                className={styles.btnDanger}
                                onClick={() => handleFileRemove('confirmation_file')}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.confirmation_file && (
                        <div className={styles.errorMessage}>{errors.confirmation_file}</div>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className={styles.formGroup}>
                    <div className={styles.textRight}>
                      <button
                        type="submit"
                        className={styles.btnHead}
                        disabled={loading}
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAccountPay; 